import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {AlertService} from "../services";
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import {finalize, first} from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";

import { locale as polish } from './i18n/pl';
import {fuseAnimations} from "../../../@fuse/animations";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Service} from "../models/service";
import {VoucherTemplateGroupService} from "../services/voucherTemplateGroup.service";
import {VoucherTemplateGroup} from "../models/voucherTemplateGroup";
import {Gallery} from "../cms/gallery/model/gallery";
import {Subscription} from "rxjs";
import {GalleryService} from "../cms/gallery/service/gallery.service";
import {HttpEventType} from "@angular/common/http";
import {GalleryItem} from "../cms/gallery/model/galleryItem";
import {UpdateGalleryItemDialogComponent} from "../cms/gallery/dialog/updateGalleryItemDialog.component";

@Component({
    templateUrl: './voucherTemplateGroupForm.component.html',
    styleUrls: ['./voucherTemplateGroupForm.component.scss'],
    animations   : fuseAnimations
})

export class VoucherTemplateGroupFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    dialogRef: any;
    availableServices: Service[];
    deleteDialogRef: any;
    processing = false;
    gallery: Gallery;
    loadingIndicator: boolean;
    @ViewChild("fileDropRef", { static: false }) fileDropEl: ElementRef;
    files: any[] = [];
    uploadSub: Subscription;
    items: string[] = [];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private _matSnackBar: MatSnackBar,
        private voucherTemplateGroupService: VoucherTemplateGroupService,
        private galleryService: GalleryService,
    ) {
        this.translationLoader.loadTranslations(polish);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Voucher template group');
    }

    ngOnInit() {
        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');

            this.galleryService.getByObject(this.id, 'VoucherTemplateGroup').subscribe((gallery: Gallery) => {
                this.gallery = gallery;

                for (let i=0; i<this.gallery.items.length; i++) {
                    this.galleryService.getImageById(this.gallery.id, this.gallery.items[i].id).pipe(first())
                        .subscribe((item:string) => {
                            this.gallery.items[i].image = item;
                        });
                }
            }, error => {

            });
        }

        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            description: [''],
            color: ['', Validators.required],
            iconFile: [''],
        });

        if (!this.isAddMode) {
            this.voucherTemplateGroupService.getById(this.id)
                .pipe(first())
                .subscribe((x: VoucherTemplateGroup) => {
                    this.f.name.setValue(x.name);
                    this.f.description.setValue(x.description);
                    this.f.color.setValue(x.color);
                });
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    selectedFile: any = null;

    onFileSelected(event: any): void {
        this.selectedFile = event.target.files[0] ?? null;
    }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        // this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createGroup();
        } else {
            this.updateGroup();
        }
    }

    /**
     * on file drop handler
     */
    onFileDropped($event) {
        this.prepareFilesList($event);
    }

    /**
     * handle file from browsing
     */
    fileBrowseHandler(files) {
        this.prepareFilesList(files);
    }

    /**
     * Convert Files list to normal array list
     * @param files (Files List)
     */
    prepareFilesList(files: Array<any>) {
        for (const item of files) {
            item.progress = 0;
            this.files.push(item);
        }
        this.fileDropEl.nativeElement.value = "";
        this.uploadFilesSimulator(0);
    }

    /**
     * Simulate the upload process
     */
    uploadFilesSimulator(index: number) {
        if (index === this.files.length) {
            return;
        } else {
            const formData = new FormData();
            formData.append("image", this.files[index]);
            formData.append("gallery", this.gallery.id);

            const upload$ = this.galleryService.uploadFile(formData).pipe(
                finalize(() => {this.reset(); this.deleteFile(index);})
            );

            this.uploadSub = upload$.subscribe(event => {
                if (event.type == HttpEventType.UploadProgress) {
                    this.files[index].progress = Math.round(100 * (event.loaded / event.total));
                }
            })
        }
    }

    /**
     * Delete file from files list
     * @param index (File index)
     */
    deleteFile(index: number) {
        if (this.files[index].progress < 100) {
            console.log("Upload in progress.");
            return;
        }
        this.files.splice(index, 1);
    }

    reset() {
        this.uploadSub = null;
        this.items = [];

        this.galleryService.getById(this.gallery.id).pipe(first()).subscribe((gallery: Gallery) => {
            this.gallery = gallery;

            for (let i=0; i<this.gallery.items.length; i++) {
                this.galleryService.getImageById(this.gallery.id, this.gallery.items[i].id).pipe(first())
                    .subscribe((item:string) => {
                        this.gallery.items[i].image = item;
                    });
            }
        });


    }

    cancelUpload() {
        this.uploadSub.unsubscribe();
        this.reset();
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     */
    formatBytes(bytes, decimals = 2) {
        if (bytes === 0) {
            return "0 Bytes";
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    showGalleryItemDialog(galleryItem: GalleryItem) {
        this.dialogRef = this._matDialog.open(UpdateGalleryItemDialogComponent, {
            panelClass: 'update-gallery-item-dialog',
            data      : {
                action : 'show',
                galleryItem: galleryItem,
            },
            width: '650px',
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    this.reset();
                    return;
                }

                let item = response.getRawValue();

                this.galleryService.updateItem(item.id, item).subscribe((res: GalleryItem) => {
                    for (let i=0; i<this.gallery.items.length; i++) {
                        if (this.gallery.items[i].id != item.id) {
                            continue;
                        }

                        this.gallery.items[i] = item;
                        this.galleryService.getImageById(this.gallery.id, this.gallery.items[i].id).pipe(first())
                            .subscribe((item:string) => {
                                this.gallery.items[i].image = item;
                            });
                    }
                }, err => {
                    console.log(err);
                });

                this.dialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    private createGroup() {
        this.processing = true;

        let values = this.form.value;
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('description', values.description);
        formData.append('color', values.color);

        if (this.selectedFile) {
            formData.append("iconFile", this.selectedFile);
            formData.append("iconSize", this.selectedFile.size);
            formData.append("iconType", this.selectedFile.type);
            formData.append("iconOriginalName", this.selectedFile.name);
        }

        this.voucherTemplateGroupService.create(formData)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Template group added successfully', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.processing = false;
                    this.router.navigate(['/settings/voucher-template-groups']);
                },
                error => {
                    this.loading = false;
                    this._matSnackBar.open('Template group creation error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                    this.processing = false;
                });
    }

    private updateGroup() {
        this.processing = true;

        let values = this.form.value;
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('description', values.description);
        formData.append('color', values.color);

        if (this.selectedFile) {
            formData.append("iconFile", this.selectedFile);
            formData.append("iconSize", this.selectedFile.size);
            formData.append("iconType", this.selectedFile.type);
            formData.append("iconOriginalName", this.selectedFile.name);
        }

        this.voucherTemplateGroupService.update(this.id, formData)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Update successful', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.processing = false;
                    this.reset();
                },
                error => {
                    this._matSnackBar.open('Template group update error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                    this.loading = false;
                    this.processing = false;
                });
    }


}
